import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getMigrationForPerson, triggerOneDriveMigration } from '@/api/onedriveMigration';
import { ApiError } from '@/api/client';
import { OneDriveMigrationBadge } from '@/components/offboarding/OneDriveMigrationBadge';
import { formatDateTime } from '@/lib/dateFormat';

/**
 * OneDrive-to-Vault migration status for this person (PR-33), reusing the
 * migration API rather than duplicating data onto oscar.Person. Shows the status
 * badge, when they became eligible, and the Vault folder link once copied. An
 * admin sees an "Archive OneDrive to Vault" button when no migration exists
 * yet or the last attempt Failed — intended primarily for historic terminated
 * records the automatic scan will never catch. The trigger skips the 7-day wait
 * and queues the person for the scheduled processor (which archives + clears).
 */
export function PersonOneDriveStatus({ personId, isAdmin, active }: { personId: number; isAdmin: boolean; active: boolean }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['onedrive-migration', personId],
    queryFn: ({ signal }) => getMigrationForPerson(personId, signal),
  });

  const trigger = useMutation({
    mutationFn: () => triggerOneDriveMigration(personId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['onedrive-migration', personId] }),
  });

  if (query.isLoading) return null;

  const migration = query.data ?? null;
  // Admin can (re-)trigger when nothing exists, it failed, or it was skipped
  // (e.g. the source was locked/unlicensed and may since have been fixed).
  const canTrigger = isAdmin && (migration === null || migration.Status === 'Failed' || migration.Status === 'Skipped');
  // ...but never for a still-active account: archiving copies then (fleet-wide)
  // DELETES the source OneDrive, so it must only run once the account is disabled
  // in Entra. Mirrors the server-side guard in OneDriveMigrationService.TriggerAsync.
  const blockedWhileActive = active;

  return (
    <div className="mt-6 border-t border-border pt-4">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-darkGrey/70">OneDrive</h3>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-darkGrey">Migration</span>
          <OneDriveMigrationBadge status={migration?.Status ?? null} />
        </div>
        {migration && (
          <div className="flex items-center gap-2">
            <span className="font-semibold text-darkGrey">Eligible since</span>
            <span className="text-darkGrey/80">{formatDateTime(migration.EligibleSince)}</span>
          </div>
        )}
        {migration?.Status === 'Copied' && migration.VaultFolderPath && (
          <div className="flex items-center gap-2">
            <span className="font-semibold text-darkGrey">Vault folder</span>
            <span className="font-mono text-xs text-darkGrey/80">{migration.VaultFolderPath}</span>
          </div>
        )}
        {migration?.SourceDeletedDate && (
          <div className="flex items-center gap-2">
            <span className="font-semibold text-darkGrey">Source cleared</span>
            <span className="text-darkGrey/80">
              {formatDateTime(migration.SourceDeletedDate)}
              {migration.SourceItemsDeleted != null ? ` · ${migration.SourceItemsDeleted} item(s)` : ''}
            </span>
          </div>
        )}
        {migration?.Status === 'Failed' && migration.Message && (
          <span className="text-xs text-red">{migration.Message}</span>
        )}
        {migration?.Status === 'Skipped' && migration.Message && (
          <span className="text-xs text-darkGrey/70">{migration.Message}</span>
        )}
        {canTrigger && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => trigger.mutate()}
              disabled={trigger.isPending || blockedWhileActive}
              title={blockedWhileActive ? 'This account is still active in Entra. Archiving is available once the account is disabled.' : undefined}
              className="rounded border border-navy/30 bg-white px-3 py-1.5 text-sm font-medium text-navy hover:bg-bg disabled:cursor-not-allowed disabled:opacity-50"
            >
              {trigger.isPending ? 'Queuing…' : 'Archive OneDrive to Vault'}
            </button>
            {blockedWhileActive && <span className="text-xs text-darkGrey/60">Account is active in Entra</span>}
          </div>
        )}
      </div>
      {trigger.isError && (
        <div role="alert" className="mt-2 rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">
          {trigger.error instanceof ApiError ? trigger.error.message : 'Could not trigger the migration.'}
        </div>
      )}
      {trigger.isSuccess && (
        <div role="status" className="mt-2 rounded border border-green/30 bg-green/10 px-3 py-2 text-sm text-green">
          Migration triggered — it will run on the next scheduled copy.
        </div>
      )}
    </div>
  );
}
